import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const API = '/api';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { login } = React.useContext(AuthContext);
  const [fullName, setFullName] = useState('');
  const [theatreName, setTheatreName] = useState('');
  const [theatreType, setTheatreType] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!fullName.trim()) {
      toast.error('Lütfen adınızı ve soyadınızı girin');
      return;
    }

    if (!theatreName.trim()) {
      toast.error('Lütfen tiyatro adını girin');
      return;
    }

    if (!theatreType) {
      toast.error('Lütfen tiyatro türünü seçin');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Şifreler eşleşmiyor');
      return;
    }

    if (password.length < 6) {
      toast.error('Şifre en az 6 karakter olmalıdır');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, {
        full_name: fullName,
        theatre_name: theatreName,
        theatre_type: theatreType,
        email,
        password,
        role: 'subscribed'  // Yeni kullanıcılar otomatik abone
      });

      login(response.data.access_token, response.data.user);
      toast.success('Hesap başarıyla oluşturuldu!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Kayıt başarısız. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="curtain-bg min-h-screen flex items-center justify-center px-6 pt-48 pb-12">
      <div className="w-full max-w-md">
        <div className="glass-effect rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <div className="flex justify-center items-center mb-4">
              <img src="/onk-logo.png" alt="ONK Ajans" style={{ width: '175px', height: 'auto' }} />
            </div>
            <h1 
              className="text-xl font-bold text-[#000000] mb-2"
              data-testid="register-title"
            >
              Dijital Oyun Metni Arşivi'ne Katılın
            </h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-form">
            <div className="space-y-2">
              <Label htmlFor="fullName" className="text-gray-300">Kullanıcının Adı ve Soyadı</Label>
              <Input
                id="fullName"
                type="text"
                placeholder="Adınız Soyadınız"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                className="bg-black/30 border-amber-800/40 text-white focus:border-amber-600 placeholder:text-[#7a7978]"
                data-testid="register-fullname-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="theatreName" className="text-gray-300">Tiyatronun Adı</Label>
              <Input
                id="theatreName"
                type="text"
                placeholder="Tiyatro adı"
                value={theatreName}
                onChange={(e) => setTheatreName(e.target.value)}
                required
                className="bg-black/30 border-amber-800/40 text-white focus:border-amber-600 placeholder:text-[#7a7978]"
                data-testid="register-theatre-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Tiyatro Türü</Label>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="theatreType"
                    value="profesyonel"
                    checked={theatreType === 'profesyonel'}
                    onChange={(e) => setTheatreType(e.target.value)}
                    required
                    className="w-4 h-4 text-[#c50605] bg-black/30 border-amber-800/40 focus:ring-[#c50605]"
                  />
                  <span className="text-white text-sm font-medium">Profesyonel</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="theatreType"
                    value="amatör"
                    checked={theatreType === 'amatör'}
                    onChange={(e) => setTheatreType(e.target.value)}
                    required
                    className="w-4 h-4 text-[#c50605] bg-black/30 border-amber-800/40 focus:ring-[#c50605]"
                  />
                  <span className="text-white text-sm font-medium">Amatör</span>
                </label>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-300">E-posta</Label>
              <Input
                id="email"
                type="email"
                placeholder="ornek@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-black/30 border-amber-800/40 text-white focus:border-amber-600 placeholder:text-[#7a7978]"
                data-testid="register-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-300">Şifre</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-black/30 border-amber-800/40 text-white focus:border-amber-600 placeholder:text-[#7a7978]"
                data-testid="register-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-gray-300">Şifreyi Onayla</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="bg-black/30 border-amber-800/40 text-white focus:border-amber-600 placeholder:text-[#7a7978]"
                data-testid="register-confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-[#c50605] hover:bg-[#9d0504] text-white py-6 text-base"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? 'Hesap oluşturuluyor...' : 'Hesap Oluştur'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-white text-sm">
              Zaten hesabınız var mı?{' '}
              <Link to="/login" className="text-white hover:text-gray-300 font-semibold underline" data-testid="register-login-link">
                Giriş yapın
              </Link>
            </p>
          </div>

          <div className="mt-4 text-center">
            <Link to="/" className="text-white hover:text-gray-300 text-sm" data-testid="register-back-link">
              ← Ana sayfaya dön
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}